import React, { FC, ReactNode, useCallback } from "react";
import { Tooltip } from "react-tooltip";
import { flip, offset, shift, type Middleware } from "@floating-ui/dom";
import { GlobalStyles } from "@mui/joy";
import {
  sanitizeSourceId,
  stripSourceRefs,
} from "../../../utils/stripSourceRefs";

export interface TooltipMetadata {
  linkedin_confidence?: number;
  [key: string]: unknown;
}

export interface TooltipData {
  index_date?: string;
  source_date?: string;
  data_comment?: string;
  source_id?: string;
  metadata?: TooltipMetadata;
}

interface AutodbTooltipWrapperProps {
  info?: TooltipData;
  children: ReactNode;
  /** Use a block-level <div> container instead of an inline <span>. Set to true when wrapping block content (cards, grids, etc.). */
  block?: boolean;
}

/**
 * Every source icon shares ONE <Tooltip>, mounted once per page as
 * <AutodbTooltipHost />.
 *
 * This used to render a <Tooltip> per wrapper. Each react-tooltip instance
 * attaches a MutationObserver to document.body with childList + subtree +
 * attributes, so a 50-row company table mounted around 2000 observers, ~1000 of
 * them watching the whole document -- and every DOM mutation anywhere in the app
 * (a spinner frame, an accordion opening, a keystroke) then fanned out to all of
 * them. Measured before this change: 40 observers per result row, and one
 * unrelated appendChild firing 250 observer callbacks at 25 rows. See
 * src/modules/bkg/views/__perf__/README.md.
 *
 * react-tooltip supports many anchors per tooltip natively: anchors carry
 * data-tooltip-id, and the host's `render` callback is handed the active anchor
 * so it can look up that anchor's own content.
 */
const TOOLTIP_ID = "autodb-tooltip";

/**
 * Anchor element -> its tooltip payload.
 *
 * Weak so entries disappear with the element itself: unmounting a page of
 * results drops its rows and the map empties with them, no cleanup effect and
 * no unmount bookkeeping that could leak if it were ever missed.
 */
const infoByAnchor = new WeakMap<HTMLElement, TooltipData>();

const TOOLTIP_STYLE: React.CSSProperties = {
  backgroundColor: "var(--joy-palette-neutral-900, #111827)",
  color: "var(--joy-palette-common-white, #ffffff)",
  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.28)",
  maxWidth: "min(560px, 90vw)",
  lineHeight: 1.35,
  zIndex: 9999,
};

// react-tooltip warns if a border arrives via `style`; it wants its own prop so
// it can mirror the border onto the arrow. Passing it here silences a console
// warning that previously fired on every BKG page render.
const TOOLTIP_BORDER = "1px solid var(--joy-palette-neutral-700, #374151)";

const TOOLTIP_LINK_STYLE: React.CSSProperties = {
  color: "var(--joy-palette-primary-200, #bfdbfe)",
  textDecoration: "underline",
  wordBreak: "break-all",
};

export type LinkedinConfidenceLevel = "high" | "medium" | "low";

export function getLinkedinConfidenceLevel(
  info?: TooltipData,
): LinkedinConfidenceLevel | null {
  const confidence = info?.metadata?.linkedin_confidence;
  if (confidence == null) return null;
  if (confidence >= 0.8) return "high";
  if (confidence >= 0.6) return "medium";
  return "low";
}

const CONFIDENCE_LABELS: Record<
  LinkedinConfidenceLevel,
  { label: string; color: string }
> = {
  high: { label: "High confidence", color: "#22c55e" },
  medium: { label: "Medium confidence", color: "#f59e0b" },
  low: { label: "Low confidence — verify manually", color: "#ef4444" },
};

const isValidUrl = (str: string): boolean => {
  try {
    const url = new URL(str);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};

/** Whether the tooltip would show anything at all for this payload. */
export const hasTooltipContent = (info?: TooltipData): boolean =>
  !!info &&
  (!!sanitizeSourceId(info.source_id) ||
    !!info.index_date ||
    !!info.source_date ||
    !!info.data_comment ||
    info.metadata?.linkedin_confidence != null);

/** The tooltip body for one anchor. Unchanged in content from the per-cell version. */
const AutodbTooltipContent: FC<{ info: TooltipData }> = ({ info }) => {
  const sanitizedSourceId = sanitizeSourceId(info.source_id);
  const level = getLinkedinConfidenceLevel(info);
  const confidence = level ? CONFIDENCE_LABELS[level] : null;

  return (
    <>
      {sanitizedSourceId && (
        <div>
          <strong>Source:</strong>{" "}
          {isValidUrl(sanitizedSourceId) ? (
            <a
              href={sanitizedSourceId}
              target="_blank"
              rel="noopener noreferrer"
              style={TOOLTIP_LINK_STYLE}
            >
              {sanitizedSourceId}
            </a>
          ) : (
            <span>{sanitizedSourceId}</span>
          )}
        </div>
      )}
      {info.index_date && (
        <div>
          <strong>Index Date:</strong>{" "}
          {info.index_date.includes("T")
            ? info.index_date.slice(0, info.index_date.indexOf("T"))
            : info.index_date}
        </div>
      )}
      {info.source_date && (
        <div>
          <strong>Source Date:</strong>{" "}
          {info.source_date.includes("T")
            ? info.source_date.slice(0, info.source_date.indexOf("T"))
            : info.source_date}
        </div>
      )}
      {info.data_comment && (
        <div>
          <strong>Comment:</strong> {stripSourceRefs(info.data_comment)}
        </div>
      )}
      {confidence && info.metadata?.linkedin_confidence != null && (
        <div>
          <strong>LinkedIn Match:</strong>{" "}
          <span style={{ color: confidence.color }}>{confidence.label}</span> (
          {(info.metadata.linkedin_confidence * 100).toFixed(0)}%)
        </div>
      )}
    </>
  );
};

/**
 * Where the tooltip may go: above its icon, else below, slid sideways to fit,
 * bounded by the viewport only.
 *
 * floating-ui's default bound is every clipping ancestor, and NavContainer's
 * <main> has `overflow-x: clip` while the page overflows it downwards. Once
 * scrolled past <main>'s own height, the tooltip was squeezed into the sliver
 * of <main> still on screen, at the top of the viewport, far from its icon.
 * react-tooltip's default also flips to the icon's side when the tooltip is
 * too wide to centre above it, where it covers the neighbouring values.
 */
const TOOLTIP_MIDDLEWARES: Middleware[] = [
  offset(10),
  flip({
    boundary: document.documentElement,
    fallbackPlacements: ["bottom"],
    crossAxis: false,
  }),
  shift({ boundary: document.documentElement, padding: 8 }),
];
// react-tooltip pushes its arrow middleware onto the array it is given, on
// every position update, so a plain array would grow as you scroll. Keep one
// middleware per name.
Object.defineProperty(TOOLTIP_MIDDLEWARES, "push", {
  value(...items: Middleware[]) {
    for (const item of items) {
      const i = TOOLTIP_MIDDLEWARES.findIndex((m) => m.name === item.name);
      if (i === -1) Array.prototype.push.call(TOOLTIP_MIDDLEWARES, item);
      else TOOLTIP_MIDDLEWARES[i] = item;
    }
    return TOOLTIP_MIDDLEWARES.length;
  },
});

const SOURCE_BUTTON_CLASS = "autodb-source-button";
const SOURCED_CLASS = "autodb-sourced";
const SOURCED_BLOCK_CLASS = "autodb-sourced--block";
const SOURCED_VALUE_CLASS = "autodb-sourced__value";

// Declared once by the host rather than per element, since a results page has
// a few hundred sourced values: as Joy IconButtons with a Material icon, 25
// result rows took twice as long to mount. See
// src/modules/bkg/views/__perf__/README.md.
const SOURCE_STYLES = {
  // A value with its source icon top-aligned beside it; as a block, the value
  // takes the full width and the icon sits in the top-right corner.
  [`.${SOURCED_CLASS}`]: {
    display: "inline-flex",
    alignItems: "flex-start",
    gap: 4,
    maxWidth: "100%",
  },
  [`.${SOURCED_BLOCK_CLASS}`]: { display: "flex" },
  // A word wider than the column would spill out of this box and under the
  // icon, so long words hyphenate, or break if they must.
  [`.${SOURCED_VALUE_CLASS}`]: {
    minWidth: 0,
    overflowWrap: "anywhere",
    hyphens: "auto",
    WebkitHyphens: "auto",
  },
  [`.${SOURCED_BLOCK_CLASS} > .${SOURCED_VALUE_CLASS}`]: { flex: 1 },
  [`.${SOURCE_BUTTON_CLASS}`]: {
    display: "inline-flex",
    flexShrink: 0,
    padding: 2,
    border: 0,
    borderRadius: "var(--joy-radius-sm, 6px)",
    background: "transparent",
    color: "var(--joy-palette-text-tertiary, #555e68)",
    cursor: "pointer",
    verticalAlign: "middle",
    "&:hover": {
      background: "var(--joy-palette-neutral-plainHoverBg, #f0f4f8)",
      color: "var(--joy-palette-text-secondary, #32383e)",
    },
    "&:focus-visible": {
      outline: "2px solid var(--joy-palette-focusVisible, #0b6bcb)",
      outlineOffset: 1,
    },
    "& svg": { width: 16, height: 16, fill: "currentColor" },
  },
};

/**
 * Mount once per page that renders AutodbTooltipWrapper or AutodbSourceButton.
 * It carries the tooltip and the styles for the values and their icons.
 */
export const AutodbTooltipHost: FC = () => (
  <>
    <GlobalStyles styles={SOURCE_STYLES} />
    <Tooltip
      id={TOOLTIP_ID}
      clickable
      delayHide={50}
      // Click too, so a tap opens it on touch screens; a tap elsewhere or Escape
      // closes it.
      openEvents={{ mouseover: true, focus: true, click: true }}
      closeEvents={{ mouseout: true, blur: true }}
      globalCloseEvents={{ escape: true, clickOutsideAnchor: true }}
      middlewares={TOOLTIP_MIDDLEWARES}
      style={TOOLTIP_STYLE}
      border={TOOLTIP_BORDER}
      render={({ activeAnchor }) => {
        const info = activeAnchor ? infoByAnchor.get(activeAnchor) : undefined;
        return info ? <AutodbTooltipContent info={info} /> : null;
      }}
    />
  </>
);

/**
 * A small info button carrying the tooltip. The value itself used to be the
 * hover target, but a tooltip there covered what you pointed at and gave no
 * sign it existed. Opens on hover, focus or tap, and renders nothing when the
 * tooltip would be empty.
 */
export const AutodbSourceButton: FC<{ info?: TooltipData }> = ({ info }) => {
  // Record the payload against the DOM node the host will hand back as
  // `activeAnchor`. Re-runs if `info` changes identity; React calls it with null
  // on the way out, which we ignore since the WeakMap entry goes with the node.
  const anchorRef = useCallback(
    (el: HTMLElement | null) => {
      if (el && info) infoByAnchor.set(el, info);
    },
    [info],
  );

  if (!hasTooltipContent(info)) return null;

  return (
    <button
      ref={anchorRef}
      type="button"
      className={SOURCE_BUTTON_CLASS}
      data-tooltip-id={TOOLTIP_ID}
      aria-label="Source details"
    >
      {/* Material's InfoOutlined */}
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M11 7h2v2h-2zm0 4h2v6h-2zm1-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2m0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8" />
      </svg>
    </button>
  );
};

// Empty strings count as nothing, as `value ?? ""` is how cells say "no value",
// and so does an element wrapping only nothing, like <Typography>{""}</Typography>.
// An element without children at all (a component given its value as props)
// counts as a value.
const hasValue = (children: ReactNode): boolean =>
  React.Children.toArray(children).some((child) => {
    if (child === "") return false;
    if (!React.isValidElement(child)) return true;
    const props = child.props as { children?: ReactNode };
    return !("children" in props) || hasValue(props.children);
  });

/**
 * A value followed by its source icon. Without a value or anything for the
 * tooltip to say, just the children.
 */
const AutodbTooltipWrapper: FC<AutodbTooltipWrapperProps> = ({
  info,
  children,
  block = false,
}) => {
  if (!hasValue(children) || !hasTooltipContent(info)) {
    return <>{children}</>;
  }

  const Container = block ? "div" : "span";
  return (
    <Container
      className={
        block ? `${SOURCED_CLASS} ${SOURCED_BLOCK_CLASS}` : SOURCED_CLASS
      }
    >
      <Container className={SOURCED_VALUE_CLASS}>{children}</Container>
      <AutodbSourceButton info={info} />
    </Container>
  );
};

export default AutodbTooltipWrapper;
